import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { live as fallbackLive, stats as fallbackStats } from '../mock';

// ---- helpers ----
function formatNumber(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
}

function shorten(s, len = 14) {
  if (!s) return '\u2014';
  return s.length > len ? s.slice(0, len) + '\u2026' : s;
}

function formatTwitchDuration(d) {
  if (!d) return '';
  const m = String(d).match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!m) return d;
  const h = Number(m[1] || 0);
  const min = Number(m[2] || 0);
  const s = Number(m[3] || 0);
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(min)}:${pad(s)}` : `${min}:${pad(s)}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}u geleden`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d geleden`;
  if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))}w geleden`;
  return `${Math.floor(diff / (86400 * 30))}mnd geleden`;
}

function getLiveStatus(data) {
  if (!data) return 'offline';
  return data.is_live ? 'live' : 'offline';
}

function mapToLive(data) {
  if (!data) return fallbackLive;
  const stream = data.stream;
  let thumb;
  if (stream) {
    thumb = stream.thumbnail_url.replace('{width}', '1280').replace('{height}', '720');
  } else {
    thumb = data.broadcaster?.offline_image_url || fallbackLive.thumb;
  }
  return {
    isLive: data.is_live,
    title: stream?.title || data.channel?.title || fallbackLive.title,
    category: stream?.game_name || data.channel?.game_name || fallbackLive.category,
    startedAt: stream?.started_at ? 'Nu live' : 'Offline',
    viewers: stream?.viewer_count || 0,
    thumb
  };
}

function mapToStats(data) {
  if (!data) return fallbackStats;
  const gameName = data.stream?.game_name || data.channel?.game_name || '\u2014';
  const subCount = data.subscriber_count;
  const subValue =
    subCount != null && subCount !== '' ? formatNumber(subCount) : '\u2014';
  return [
    {
      label: 'Game',
      value: shorten(gameName),
      image: data.game_box_art_url || null
    },
    { label: 'Volgers', value: formatNumber(data.followers) },
    { label: 'Subs', value: subValue },
    { label: 'Status', value: data.is_live ? 'LIVE' : 'Offline' }
  ];
}

function mapVodList(rawList) {
  return (rawList || []).map((v) => {
    const thumb = (v.thumbnail_url || '')
      .replace('%{width}', '640')
      .replace('%{height}', '360');
    return {
      id: v.id,
      title: v.title || '(geen titel)',
      game: v.archived ? (v.game_name || 'Archief') : 'Twitch VOD',
      duration: formatTwitchDuration(v.duration),
      views: v.view_count || 0,
      date: timeAgo(v.created_at),
      createdAt: v.created_at ? Date.parse(v.created_at) : 0,
      thumb,
      twitchUrl: v.url,
      archived: Boolean(v.archived),
      youtubeId: null
    };
  });
}

function formatSeconds(s) {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

function mapClipList(rawList) {
  return (rawList || []).map((c) => ({
    id: c.id,
    title: c.title || '(geen titel)',
    game: 'Clip',
    duration: formatSeconds(c.duration),
    views: c.view_count || 0,
    date: timeAgo(c.created_at),
    createdAt: c.created_at ? Date.parse(c.created_at) : 0,
    thumb: c.thumbnail_url,
    twitchUrl: c.url,
    clipId: c.id,
    embedUrl: c.embed_url,
    youtubeId: null
  }));
}

// ---- fetchers ----
async function fetchChannelData() {
  try {
    const resp = await api.get('/twitch/channel');
    return { data: resp.data, error: null };
  } catch (err) {
    console.error('twitch/channel failed', err);
    return { data: null, error: err.message };
  }
}

async function fetchVodList() {
  try {
    const resp = await api.get('/twitch/vods');
    return mapVodList(resp.data?.vods);
  } catch (err) {
    console.error('twitch/vods failed', err);
    return null;
  }
}

async function fetchClipList() {
  try {
    const resp = await api.get('/twitch/clips');
    return mapClipList(resp.data?.clips);
  } catch (err) {
    console.error('twitch/clips failed', err);
    return null;
  }
}

// ---- hooks ----
export function useTwitchChannel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetchChannelData().then((res) => {
      if (!active) return;
      setData(res.data);
      setError(res.error);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const live = useMemo(() => mapToLive(data), [data]);
  const stats = useMemo(() => mapToStats(data), [data]);
  const status = useMemo(() => getLiveStatus(data), [data]);

  return { data, live, stats, status, loading, error };
}

export function useTwitchVods() {
  const [vods, setVods] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchVodList().then((list) => {
      if (!active) return;
      setVods(list || []);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { vods: vods || [], loading };
}

export function useTwitchClips() {
  const [clips, setClips] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchClipList().then((list) => {
      if (!active) return;
      setClips(list || []);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { clips: clips || [], loading };
}
